import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {Group, Stack, Text, UnstyledButton} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import AIMenu from 'llm/chat-library/ui/AIMenu';
import {AI_GRAY_CHEVRON} from 'llm/chat-library/ui/AIStyleConstants';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import useFuzzyDate from 'ui-library/hooks/useFuzzyDate';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';

import {getChatNameRenderInfo} from '../ChatNameUtil';
import {
  CHAT_SESSION_MENU_FRAGMENT,
  useChatSessionMenuOptions,
  type ChatSessionStarUpdate,
} from '../ChatSessionMenu';
import {
  RedesignHeaderChatTitleQuery,
  RedesignHeaderChatTitleQueryVariables,
} from './__generated/RedesignHeaderChatTitleQuery';

export const REDESIGN_HEADER_CHAT_TITLE_QUERY = gql`
  query RedesignHeaderChatTitleQuery($id: StrictString!) @engComponent(name: LLM) @skipAudit {
    chatSession(id: $id) {
      ... on ChatSessionNode {
        ...ChatSessionMenu
        origin {
          __typename
          ... on ChatRoutineMessageOriginNode {
            routine {
              __typename
              ... on RoutineNode {
                id
                runAs
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${CHAT_SESSION_MENU_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * The active chat's name (and last-modified time) shown centered in the redesigned sidepanel header.
 * Reads the chat session cache-first, so it resolves immediately once the chat has been opened.
 */
export default function RedesignHeaderTitle({
  chatId,
  onStarUpdated,
}: {
  chatId: string;
  onStarUpdated?: (update: ChatSessionStarUpdate) => Promise<void>;
}): JSX.Element | null {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const result = useBenchlingQuery<RedesignHeaderChatTitleQuery, RedesignHeaderChatTitleQueryVariables>(
    REDESIGN_HEADER_CHAT_TITLE_QUERY,
    {variables: {id: chatId}, fetchPolicy: 'cache-first', renderCachedDataWhenRefetching: true}
  );
  const node =
    result.type === QueryResultType.SUCCESS && !isInaccessible(result.data.chatSession)
      ? result.data.chatSession
      : null;
  const fuzzyDate = useFuzzyDate(node?.modifiedAt ?? new Date().toISOString());

  const menuOptions = useChatSessionMenuOptions({
    chatSession: node ?? {id: chatId, name: null},
    onStarUpdated,
  });

  const menu = useMemo(
    () => (
      <AIMenu scrollable={false} height={MenuHeight.SMALL} width={MenuWidth.FIT_CONTENT}>
        <Section>
          {menuOptions.map((option, index) => (
            <Option
              key={index}
              icon={option.icon}
              isDisabled={option.isDisabled}
              onSelect={() => {
                option.onSelect?.();
                setIsMenuOpen(false);
              }}
            >
              {option.contents}
            </Option>
          ))}
        </Section>
      </AIMenu>
    ),
    [menuOptions]
  );

  if (node == null) {
    return null;
  }

  const nameInfo = getChatNameRenderInfo({id: node.id, name: node.name});
  // Own the heading role here (rather than on the wrapping header slot) so it exists only once the
  // title has resolved — while this returns null the header has no empty heading for screen readers.

  return (
    <div {...getTestProps({component: 'RedesignHeaderTitle', element: 'root'})}>
      <Stack align="center" aria-label={nameInfo.text} aria-level={1} miw={0} role="heading">
        <AbsoluteDropdownContainer
          menu={menu}
          onRequestClose={() => {
            setIsMenuOpen(false);
          }}
          open={isMenuOpen}
          shouldCloseOnInsideClick={true}
          shouldCloseOnOutsideClick={true}
          canDropUnder={false}
          alwaysDropDown={true}
        >
          <Stack align="center" maw="100%" miw={0} spacing={0} w="fit-content">
            <UnstyledButton
              aria-expanded={isMenuOpen}
              aria-haspopup="menu"
              aria-label="Chat options"
              display="inline-flex"
              maw="100%"
              miw={0}
              onClick={() => {
                setIsMenuOpen(true);
              }}
            >
              <Group maw="100%" miw={0} noWrap={true} spacing="xs">
                <Text component="span" fw={600} miw={0} size="md" sx={{flex: 1}} truncate={true}>
                  {nameInfo.node}
                </Text>
                <BenchlingIcon color={AI_GRAY_CHEVRON} icon="chevron-down" />
              </Group>
            </UnstyledButton>
            <Text c="dimmed" size="xs">
              Last modified {fuzzyDate}
            </Text>
          </Stack>
        </AbsoluteDropdownContainer>
      </Stack>
    </div>
  );
}
